import React, {useState, useContext} from "react";
import {withAuth} from "../../hoc/Auth";
import Api from "../../Api";
import DateRange from "../../Components/Utils/DateRange";
import MessageContext from "../../MessageContext";
import Loader from "../../Components/Utils/Loader";
import * as XLSX from "xlsx";

import classes from "./styles.module.css";

// --- Auto-detect provider format by column names ---
// NX (PayCraft/CraftLite) — файлы с колонкой "ID операции мерчанта"
// Flacon (Hydracode/HydraLite) — два формата:
//   - формат с "Reference id" + "amount" (lowercase) — матчим по provider order ID
//   - формат с "Reference id" + "Amount" (capital) + payment_NNNNN — матчим по нашему ID

function detectFormat(workbook) {
    for (var i = 0; i < workbook.SheetNames.length; i++) {
        var sheet = workbook.Sheets[workbook.SheetNames[i]];
        var data = XLSX.utils.sheet_to_json(sheet, {defval: "", header: 1});
        if (data.length === 0) continue;
        var headers = data[0];
        if (!headers) continue;
        var headerStr = headers.join("|");

        // NX: has "ID операции мерчанта"
        if (headerStr.indexOf("ID операции мерчанта") !== -1) return "nx";
        // Flacon format 2: has "Reference id" + "Amount" (capital A) — Platon/PAY2 terminal
        if (headerStr.indexOf("Reference id") !== -1 && headerStr.indexOf("Amount") !== -1) return "flacon_pay2";
        // Flacon format 1: has "Reference id" + "amount" (lowercase)
        if (headerStr.indexOf("Reference id") !== -1 && headerStr.indexOf("amount") !== -1) return "flacon_ref";
    }
    return null;
}

var FORMAT_LABELS = {
    nx: "NX (PayCraft)",
    flacon_ref: "Flacon (Hydracode)",
    flacon_pay2: "Flacon (Hydracode / PAY2)"
};

function parseNX(workbook) {
    var rows = [];
    for (var i = 0; i < workbook.SheetNames.length; i++) {
        var sheet = workbook.Sheets[workbook.SheetNames[i]];
        var data = XLSX.utils.sheet_to_json(sheet, {defval: ""});
        for (var j = 0; j < data.length; j++) {
            var r = data[j];
            var id = r["ID операции мерчанта"];
            var amount = parseFloat(r["Сумма в RUB"]);
            if (id && !isNaN(amount)) {
                rows.push({id: String(id), amount: amount, date: r["Дата и время"] || ""});
            }
        }
    }
    return rows;
}

function parseFlaconRef(workbook) {
    var rows = [];
    for (var i = 0; i < workbook.SheetNames.length; i++) {
        var sheet = workbook.Sheets[workbook.SheetNames[i]];
        var data = XLSX.utils.sheet_to_json(sheet, {defval: ""});
        for (var j = 0; j < data.length; j++) {
            var r = data[j];
            var refId = r["Reference id"];
            var amount = parseFloat(r["amount"]);
            if (refId && !isNaN(amount)) {
                rows.push({id: String(refId), amount: amount, date: r["date"] || ""});
            }
        }
    }
    return rows;
}

function parseFlaconPay2(workbook) {
    var rows = [];
    var sheetNames = workbook.SheetNames;
    var targetSheets = [];
    for (var i = 0; i < sheetNames.length; i++) {
        if (sheetNames[i].toLowerCase().indexOf("transaction") !== -1) {
            targetSheets.push(sheetNames[i]);
        }
    }
    if (targetSheets.length === 0) targetSheets = sheetNames;

    for (var si = 0; si < targetSheets.length; si++) {
        var sheet = workbook.Sheets[targetSheets[si]];
        var data = XLSX.utils.sheet_to_json(sheet, {defval: ""});
        for (var j = 0; j < data.length; j++) {
            var r = data[j];
            var refId = r["Reference id"] || "";
            var amount = parseFloat(r["Amount"]);
            var match = String(refId).match(/payment_(\d+)/);
            var id = match ? match[1] : "";
            if (id && !isNaN(amount)) {
                rows.push({id: id, amount: amount, date: r["Created"] || ""});
            }
        }
    }
    return rows;
}

var PARSERS = {
    nx: parseNX,
    flacon_ref: parseFlaconRef,
    flacon_pay2: parseFlaconPay2
};

// NX и Flacon PAY2 — матчим по нашему transaction ID
// Flacon ref — матчим по provider order ID (вторая часть processingOrderId)
function buildOurIndex(transactions, format) {
    var index = {};
    for (var i = 0; i < transactions.length; i++) {
        var tx = transactions[i];
        if (format === "flacon_ref") {
            // processingOrderId = "ourId|providerOrderId"
            var parts = (tx.processingOrderId || "").split("|");
            var key = parts.length > 1 ? parts[1] : "";
            if (key) {
                index[key] = tx;
            }
        } else {
            // NX, Flacon PAY2 — match by our transaction ID
            if (tx.id) {
                index[String(tx.id)] = tx;
            }
        }
    }
    return index;
}

function reconcile(providerRows, ourTransactions, format) {
    var ourIndex = buildOurIndex(ourTransactions, format);

    var matched = [];
    var onlyInFile = [];
    var amountMismatch = [];

    var matchedOurKeys = {};

    for (var i = 0; i < providerRows.length; i++) {
        var row = providerRows[i];
        var ourTx = ourIndex[row.id];
        if (!ourTx) {
            onlyInFile.push({providerRow: row});
        } else {
            var key = format === "flacon_ref"
                ? (function() { var p = (ourTx.processingOrderId || "").split("|"); return p.length > 1 ? p[1] : ""; })()
                : String(ourTx.id);
            matchedOurKeys[key] = true;

            var ourAmount = parseFloat(ourTx.amount) || 0;
            var theirAmount = row.amount;
            if (Math.abs(ourAmount - theirAmount) > 0.01) {
                amountMismatch.push({providerRow: row, ourTx: ourTx, ourAmount: ourAmount, theirAmount: theirAmount});
            } else {
                matched.push({providerRow: row, ourTx: ourTx});
            }
        }
    }

    // Transactions we have but not in file
    var onlyInOurs = [];
    var keys = Object.keys(ourIndex);
    for (var k = 0; k < keys.length; k++) {
        if (!matchedOurKeys[keys[k]]) {
            onlyInOurs.push({ourTx: ourIndex[keys[k]]});
        }
    }

    return {
        matched: matched,
        onlyInOurs: onlyInOurs,
        onlyInFile: onlyInFile,
        amountMismatch: amountMismatch
    };
}

// Load all pages of transactions
function loadAllTransactions(api, dateRange, page, accumulated, callback, onError) {
    var ITEMS_PER_PAGE = 500;
    var payload = {
        filters: [],
        dateRange: {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        },
        page: page,
        itemsPerPage: ITEMS_PER_PAGE
    };

    api.setTransactionsFilters(payload).then(function(r) {
        var data = r && r.data ? r.data : [];
        var all = accumulated.concat(data);
        var totalCount = r && r.count && r.count.total ? Number(r.count.total.count) : 0;

        if (all.length < totalCount && data.length > 0) {
            loadAllTransactions(api, dateRange, page + 1, all, callback, onError);
        } else {
            callback(all);
        }
    }).catch(function(err) {
        onError(err);
    });
}

var Reconciliation = withAuth(function(props) {
    var token = props.token;
    var onMessage = useContext(MessageContext).onMessage;

    var _filterDate = useState({});
    var filterDate = _filterDate[0];
    var setFilterDate = _filterDate[1];

    var _file = useState(null);
    var file = _file[0];
    var setFile = _file[1];

    var _drag = useState(false);
    var drag = _drag[0];
    var setDrag = _drag[1];

    var _loading = useState(false);
    var loading = _loading[0];
    var setLoading = _loading[1];

    var _result = useState(null);
    var result = _result[0];
    var setResult = _result[1];

    var _activeTab = useState("onlyInOurs");
    var activeTab = _activeTab[0];
    var setActiveTab = _activeTab[1];

    var _detectedProvider = useState(null);
    var detectedProvider = _detectedProvider[0];
    var setDetectedProvider = _detectedProvider[1];

    var dateFrom = filterDate.startDate || "";
    var dateTo = filterDate.endDate || "";

    // File handling
    function handleFile(f) {
        if (!f) return;
        var ext = f.name.split(".").pop().toLowerCase();
        if (ext !== "xlsx" && ext !== "xls") {
            onMessage({type: "error", message: "Поддерживаются только файлы .xlsx / .xls"});
            return;
        }
        setFile(f);
        setResult(null);
    }

    function onDragStart(e) { e.preventDefault(); setDrag(true); }
    function onDragLeave(e) { e.preventDefault(); setDrag(false); }
    function onDrop(e) {
        e.preventDefault();
        setDrag(false);
        var files = e.dataTransfer && e.dataTransfer.files;
        if (files && files.length > 0) handleFile(files[0]);
    }
    function onClickUpload() {
        var input = document.createElement("input");
        input.type = "file";
        input.accept = ".xlsx,.xls";
        input.onchange = function(e) {
            if (e.target && e.target.files && e.target.files.length > 0) {
                handleFile(e.target.files[0]);
            }
        };
        input.click();
    }

    function removeFile() {
        setFile(null);
        setResult(null);
        setDetectedProvider(null);
    }

    // Reconciliation
    function handleReconcile() {
        if (!file) {
            onMessage({type: "error", message: "Загрузите файл"});
            return;
        }
        if (!dateFrom || !dateTo) {
            onMessage({type: "error", message: "Выберите период"});
            return;
        }

        setLoading(true);
        setResult(null);

        // 1. Parse Excel
        var reader = new FileReader();
        reader.onload = function(e) {
            var data = new Uint8Array(e.target.result);
            var workbook;
            try {
                workbook = XLSX.read(data, {type: "array"});
            } catch (err) {
                setLoading(false);
                onMessage({type: "error", message: "Ошибка чтения файла: " + err.message});
                return;
            }

            var format = detectFormat(workbook);
            if (!format) {
                setLoading(false);
                onMessage({type: "error", message: "Не удалось определить формат файла. Поддерживаются: NX, Flacon."});
                return;
            }
            setDetectedProvider(format);

            var parser = PARSERS[format];
            var providerRows;
            try {
                providerRows = parser(workbook);
            } catch (err) {
                setLoading(false);
                onMessage({type: "error", message: "Ошибка парсинга: " + err.message});
                return;
            }

            if (providerRows.length === 0) {
                setLoading(false);
                onMessage({type: "warning", message: "Не найдено транзакций в файле. Проверьте формат."});
                return;
            }

            // 2. Load our transactions
            var api = new Api({token: token});
            loadAllTransactions(api, {startDate: dateFrom, endDate: dateTo}, 1, [], function(ourTransactions) {
                // 3. Reconcile
                var res = reconcile(providerRows, ourTransactions, format);
                setResult(res);
                setActiveTab(
                    res.onlyInOurs.length > 0 ? "onlyInOurs" :
                    res.onlyInFile.length > 0 ? "onlyInFile" :
                    res.amountMismatch.length > 0 ? "amountMismatch" : "matched"
                );
                setLoading(false);
            }, function() {
                setLoading(false);
                onMessage({type: "error", message: "Ошибка загрузки транзакций"});
            });
        };
        reader.readAsArrayBuffer(file);
    }

    var _search = useState("");
    var search = _search[0];
    var setSearch = _search[1];

    var TAB_DESCRIPTIONS = {
        matched: "Транзакции, найденные и у нас, и в файле провайдера. Суммы совпадают (допуск \u00b10.01).",
        onlyInOurs: "Транзакции, которые есть в нашей системе за выбранный период, но отсутствуют в загруженном файле провайдера. Возможная причина: провайдер не учёл транзакцию или она прошла через другой канал.",
        onlyInFile: "Транзакции из файла провайдера, которые не найдены в нашей системе. Возможная причина: транзакция не была зарегистрирована у нас или ID не совпадает.",
        amountMismatch: "Транзакции найдены с обеих сторон, но суммы отличаются. Требуется ручная проверка."
    };

    function filterItems(items) {
        if (!search) return items;
        var q = search.toLowerCase();
        return items.filter(function(item) {
            var parts = [];
            if (item.ourTx) {
                parts.push(String(item.ourTx.id));
                parts.push(String(item.ourTx.amount));
                parts.push(item.ourTx.merchantName || "");
                parts.push(item.ourTx.status || "");
            }
            if (item.providerRow) {
                parts.push(String(item.providerRow.id));
                parts.push(String(item.providerRow.amount));
                parts.push(item.providerRow.date || "");
            }
            if (item.ourAmount != null) parts.push(String(item.ourAmount));
            if (item.theirAmount != null) parts.push(String(item.theirAmount));
            return parts.join(" ").toLowerCase().indexOf(q) !== -1;
        });
    }

    function handleExportExcel() {
        if (!result) return;
        var wb = XLSX.utils.book_new();

        // Sheet 1: Совпало
        if (result.matched.length > 0) {
            var matchedData = result.matched.map(function(item) {
                return {
                    "Наш ID": item.ourTx.id,
                    "ID в файле": item.providerRow.id,
                    "Сумма": (parseFloat(item.ourTx.amount) || 0).toFixed(2),
                    "Мерчант": item.ourTx.merchantName || "",
                    "Дата (файл)": item.providerRow.date
                };
            });
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(matchedData), "Совпало (" + result.matched.length + ")");
        }

        // Sheet 2: Только у нас
        if (result.onlyInOurs.length > 0) {
            var onlyOursData = result.onlyInOurs.map(function(item) {
                var tx = item.ourTx;
                return {
                    "ID": tx.id,
                    "Сумма": (parseFloat(tx.amount) || 0).toFixed(2),
                    "Дата": tx.date || "",
                    "Статус": tx.status || "",
                    "Мерчант": tx.merchantName || ""
                };
            });
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(onlyOursData), "Только у нас (" + result.onlyInOurs.length + ")");
        }

        // Sheet 3: Только в реестре
        if (result.onlyInFile.length > 0) {
            var onlyFileData = result.onlyInFile.map(function(item) {
                return {
                    "ID в файле": item.providerRow.id,
                    "Сумма": item.providerRow.amount.toFixed(2),
                    "Дата": item.providerRow.date
                };
            });
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(onlyFileData), "Только в реестре (" + result.onlyInFile.length + ")");
        }

        // Sheet 4: Расхождение суммы
        if (result.amountMismatch.length > 0) {
            var mismatchData = result.amountMismatch.map(function(item) {
                return {
                    "ID": item.ourTx.id,
                    "Наша сумма": item.ourAmount.toFixed(2),
                    "Сумма в файле": item.theirAmount.toFixed(2),
                    "Разница": (item.ourAmount - item.theirAmount).toFixed(2),
                    "Мерчант": item.ourTx.merchantName || "",
                    "Дата (файл)": item.providerRow.date
                };
            });
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(mismatchData), "Расхождение (" + result.amountMismatch.length + ")");
        }

        // Summary sheet
        var summaryData = [
            {"Категория": "Совпало", "Количество": result.matched.length},
            {"Категория": "Только у нас", "Количество": result.onlyInOurs.length},
            {"Категория": "Только в реестре", "Количество": result.onlyInFile.length},
            {"Категория": "Расхождение суммы", "Количество": result.amountMismatch.length},
            {"Категория": "", "Количество": ""},
            {"Категория": "Провайдер", "Количество": FORMAT_LABELS[detectedProvider] || ""},
            {"Категория": "Период", "Количество": dateFrom + " — " + dateTo},
            {"Категория": "Файл", "Количество": file ? file.name : ""}
        ];
        var summarySheet = XLSX.utils.json_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(wb, summarySheet, "Итого");

        var filename = "Сверка_" + (dateFrom || "").replace(/[^0-9]/g, "") + "_" + (dateTo || "").replace(/[^0-9]/g, "") + ".xlsx";
        XLSX.writeFile(wb, filename);
    }

    function renderTable() {
        if (!result) return null;

        var items = filterItems(result[activeTab] || []);

        if (items.length === 0 && !search) {
            return React.createElement("div", {className: "text-center text-muted py-4"}, "Нет записей");
        }
        if (items.length === 0 && search) {
            return React.createElement("div", {className: "text-center text-muted py-4"}, "Ничего не найдено по запросу \"" + search + "\"");
        }

        if (activeTab === "matched") {
            return React.createElement("div", {style: {overflowX: "auto"}},
                React.createElement("table", {className: "table table-hover table-sm"},
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("th", null, "Наш ID"),
                            React.createElement("th", null, "ID в файле"),
                            React.createElement("th", null, "Сумма"),
                            React.createElement("th", null, "Мерчант"),
                            React.createElement("th", null, "Дата (файл)")
                        )
                    ),
                    React.createElement("tbody", null,
                        items.map(function(item, idx) {
                            return React.createElement("tr", {key: idx},
                                React.createElement("td", null, item.ourTx.id),
                                React.createElement("td", null, item.providerRow.id),
                                React.createElement("td", null, (parseFloat(item.ourTx.amount) || 0).toFixed(2)),
                                React.createElement("td", null, item.ourTx.merchantName || ""),
                                React.createElement("td", {style: {whiteSpace: "nowrap"}}, item.providerRow.date)
                            );
                        })
                    )
                )
            );
        }

        if (activeTab === "onlyInOurs") {
            return React.createElement("div", {style: {overflowX: "auto"}},
                React.createElement("table", {className: "table table-hover table-sm"},
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("th", null, "ID"),
                            React.createElement("th", null, "Сумма"),
                            React.createElement("th", null, "Дата"),
                            React.createElement("th", null, "Статус"),
                            React.createElement("th", null, "Мерчант")
                        )
                    ),
                    React.createElement("tbody", null,
                        items.map(function(item, idx) {
                            var tx = item.ourTx;
                            return React.createElement("tr", {key: idx},
                                React.createElement("td", null, tx.id),
                                React.createElement("td", null, (parseFloat(tx.amount) || 0).toFixed(2)),
                                React.createElement("td", {style: {whiteSpace: "nowrap"}}, tx.date ? new Date(tx.date).toLocaleString("ru-RU") : ""),
                                React.createElement("td", null, tx.status || ""),
                                React.createElement("td", null, tx.merchantName || "")
                            );
                        })
                    )
                )
            );
        }

        if (activeTab === "onlyInFile") {
            return React.createElement("div", {style: {overflowX: "auto"}},
                React.createElement("table", {className: "table table-hover table-sm"},
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("th", null, "ID в файле"),
                            React.createElement("th", null, "Сумма"),
                            React.createElement("th", null, "Дата")
                        )
                    ),
                    React.createElement("tbody", null,
                        items.map(function(item, idx) {
                            return React.createElement("tr", {key: idx},
                                React.createElement("td", null, item.providerRow.id),
                                React.createElement("td", null, item.providerRow.amount.toFixed(2)),
                                React.createElement("td", null, item.providerRow.date)
                            );
                        })
                    )
                )
            );
        }

        if (activeTab === "amountMismatch") {
            return React.createElement("div", {style: {overflowX: "auto"}},
                React.createElement("table", {className: "table table-hover table-sm"},
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("th", null, "ID"),
                            React.createElement("th", null, "Наша сумма"),
                            React.createElement("th", null, "Сумма в файле"),
                            React.createElement("th", null, "Разница"),
                            React.createElement("th", null, "Мерчант"),
                            React.createElement("th", null, "Дата (файл)")
                        )
                    ),
                    React.createElement("tbody", null,
                        items.map(function(item, idx) {
                            var diff = item.ourAmount - item.theirAmount;
                            return React.createElement("tr", {key: idx},
                                React.createElement("td", null, item.ourTx.id),
                                React.createElement("td", null, item.ourAmount.toFixed(2)),
                                React.createElement("td", null, item.theirAmount.toFixed(2)),
                                React.createElement("td", {className: diff > 0 ? "text-success" : "text-danger"},
                                    (diff > 0 ? "+" : "") + diff.toFixed(2)
                                ),
                                React.createElement("td", null, item.ourTx.merchantName || ""),
                                React.createElement("td", {style: {whiteSpace: "nowrap"}}, item.providerRow.date)
                            );
                        })
                    )
                )
            );
        }

        return null;
    }

    return (
        <div className={classes.ReconciliationWrapper}>
            <div className={classes.containerFluid}>
                <div className="row">

                    <div className="col-12 pt-2">
                        <h4>Сверка</h4>
                        <p className="text-muted mb-3" style={{fontSize: ".85rem"}}>
                            Сравнение транзакций между нашей системой и реестром провайдера.
                            Загрузите Excel-файл от провайдера, выберите период и нажмите «Сверить».
                            Формат файла определяется автоматически (NX, Flacon).
                        </p>

                        {/* Filters */}
                        <div className={classes.FiltersCard}>
                            <div className={classes.FiltersRow}>
                                <div className={classes.FilterGroup}>
                                    <DateRange onSelectDate={function(range) { setFilterDate(range); setResult(null); }} defaultPeriod="week"/>
                                </div>
                                <div className={classes.FilterGroup}>
                                    <button
                                        className={"btn btn-primary " + classes.ReconcileBtn}
                                        onClick={handleReconcile}
                                        disabled={loading || !file || !dateFrom || !dateTo}
                                    >
                                        {loading ? "Сверка..." : "Сверить"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* File upload */}
                        {!file ? (
                            <div
                                className={classes.UploadZone + (drag ? " " + classes.UploadZoneDrag : "")}
                                onClick={onClickUpload}
                                onDrop={onDrop}
                                onDragStart={onDragStart}
                                onDragLeave={onDragLeave}
                                onDragOver={onDragStart}
                            >
                                Перетащите Excel-файл от провайдера или нажмите для выбора
                            </div>
                        ) : (
                            <div className={classes.FileName}>
                                {file.name}
                                <span className={classes.FileRemove} onClick={removeFile} title="Удалить">&times;</span>
                            </div>
                        )}

                        {/* Loading */}
                        {loading && <Loader/>}

                        {/* Results */}
                        {result && !loading && (
                            <div>
                                {/* Provider + export */}
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    {detectedProvider && (
                                        <span className="text-muted" style={{fontSize: ".85rem"}}>
                                            Провайдер: <b>{FORMAT_LABELS[detectedProvider] || detectedProvider}</b>
                                            {" \u00b7 "}
                                            Транзакций в файле: <b>{result.matched.length + result.onlyInFile.length + result.amountMismatch.length}</b>
                                            {" \u00b7 "}
                                            Транзакций у нас: <b>{result.matched.length + result.onlyInOurs.length + result.amountMismatch.length}</b>
                                        </span>
                                    )}
                                    <button
                                        className="btn btn-outline-success btn-sm"
                                        onClick={handleExportExcel}
                                        style={{whiteSpace: "nowrap"}}
                                    >
                                        Экспорт в Excel
                                    </button>
                                </div>

                                {/* Summary cards */}
                                <div className={classes.SummaryRow}>
                                    <div
                                        className={classes.summaryCard + (activeTab === "matched" ? " " + classes.summaryCardActive : "")}
                                        onClick={function() { setActiveTab("matched"); setSearch(""); }}
                                    >
                                        <small>Совпало</small>
                                        <h5 className="text-success">{result.matched.length}</h5>
                                    </div>
                                    <div
                                        className={classes.summaryCard + (activeTab === "onlyInOurs" ? " " + classes.summaryCardActive : "")}
                                        onClick={function() { setActiveTab("onlyInOurs"); setSearch(""); }}
                                    >
                                        <small>Только у нас</small>
                                        <h5 className={result.onlyInOurs.length > 0 ? "text-danger" : ""}>{result.onlyInOurs.length}</h5>
                                    </div>
                                    <div
                                        className={classes.summaryCard + (activeTab === "onlyInFile" ? " " + classes.summaryCardActive : "")}
                                        onClick={function() { setActiveTab("onlyInFile"); setSearch(""); }}
                                    >
                                        <small>Только в реестре</small>
                                        <h5 className={result.onlyInFile.length > 0 ? "text-danger" : ""}>{result.onlyInFile.length}</h5>
                                    </div>
                                    <div
                                        className={classes.summaryCard + (activeTab === "amountMismatch" ? " " + classes.summaryCardActive : "")}
                                        onClick={function() { setActiveTab("amountMismatch"); setSearch(""); }}
                                    >
                                        <small>Расхождение суммы</small>
                                        <h5 className={result.amountMismatch.length > 0 ? "text-warning" : ""}>{result.amountMismatch.length}</h5>
                                    </div>
                                </div>

                                {/* Tab description */}
                                <div className={classes.tabDescription}>
                                    {TAB_DESCRIPTIONS[activeTab]}
                                </div>

                                {/* Search */}
                                <div className="mb-2">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Поиск по ID, сумме, мерчанту..."
                                        value={search}
                                        onChange={function(e) { setSearch(e.target.value); }}
                                        style={{maxWidth: 350}}
                                    />
                                </div>

                                {/* Detail table */}
                                {renderTable()}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default Reconciliation;



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/Reconciliation/index.js